import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Container } from '@material-ui/core';

import {
  createBrand,
  getCountries,
  getStockExchanges,
  getUsStates,
  getValidationRules,
  getVerticalTypes,
} from '../apis';
import {
  BrandDetailsForm,
  BrandRelationshipForm,
  ConfirmationDialog,
  ContactDetailsForm,
  Stepper,
} from '../components';
import {
  BrandDetailsFormValues,
  BrandRelationshipFormValues,
  ContactDetailsFormValues,
  ValidationRule,
} from '../types';
import { findCmsRecordByKey } from '../utils';
import { selectDynamicContent } from '@/applications/CspPortal/CSP-Form/selectors';
import { S3_ASSETS_PATH } from '@/constants';
import { HelpSection, SuccessDialogue } from '@/shared_elements';
import { EntityTypes } from '@/shared_elements/enums';
import { SelectItem } from '@/utils/types';

import '@/assets/styles/csp-register-module.scss';

enum ModalType {
  None = 'NONE',
  Confirmation = 'CONFIRMATION',
  Success = 'SUCCESS',
}

const Steps = [
  {
    key: 'brandDetails',
    label: 'Brand Details',
  },
  {
    key: 'trustScore',
    label: 'Brand Relationship',
  },
  {
    key: 'contactDetails',
    label: 'Contact Details',
  },
];

const BrandRegistration: FunctionComponent = () => {
  const dynamicContent = useSelector(selectDynamicContent);
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [countries, setCountries] = useState<SelectItem[]>([]);
  const [usStates, setUsStates] = useState<SelectItem[]>([]);
  const [stockExchanges, setStockExchanges] = useState<SelectItem[]>([]);
  const [verticalTypes, setVerticalTypes] = useState<SelectItem[]>([]);
  const [validationRules, setValidationRules] = useState<ValidationRule[]>([]);
  const [brandDetailsFormValues, setBrandDetailsFormValues] =
    useState<BrandDetailsFormValues>({
      companyName: '',
      displayName: '',
      entityType: '',
      country: '',
      ein: '',
      einIssuingCountry: '',
      altBusinessIdType: '',
      altBusinessId: '',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      website: '',
      stockSymbol: '',
      stockExchange: '',
      vertical: '',
      referenceId: '',
      firstName: '',
      lastName: '',
      mobilePhone: '',
    });
  const [brandRelationshipFormValues, setBrandRelationshipFormValues] =
    useState<BrandRelationshipFormValues>({
      brandRelationshipScore: 0,
    });
  const [contactDetailsFormValues, setContactDetailsFormValues] =
    useState<ContactDetailsFormValues>({
      email: '',
      phone: '',
      businessContactEmail: '',
    });
  const [modalType, setModalType] = useState(ModalType.None);
  const [proceeding, setProceeding] = useState(false);
  const [registeredBrandUid, setRegisteredBrandUid] = useState('');
  const paymentInfoText = (
    <p>
      A one-time, non-refundable payment of $
      {brandDetailsFormValues.entityType === 'SOLE_PROPRIETOR'
        ? '4.00'
        : '4.50'}{' '}
      is required to register each BRAND. It will be added to your next balance.
      <br />
      Click <strong>I agree to confirm</strong>.
    </p>
  );
  const successInfoText = useMemo(
    () =>
      findCmsRecordByKey(dynamicContent, 'csp-create-brand-form#success')
        ?.text ?? '',
    [dynamicContent]
  );

  const proceed = async () => {
    setProceeding(true);
    const data = {
      ...brandDetailsFormValues,
      ...brandRelationshipFormValues,
      ...contactDetailsFormValues,
      nonBlocking: true,
      phone:
        formatPhoneNumberIntl(contactDetailsFormValues.phone) ||
        contactDetailsFormValues.phone,
    };
    const brand = await createBrand(data);
    if (brand) {
      setRegisteredBrandUid(brand.uid!);
      setModalType(ModalType.Success);
    } else {
      setModalType(ModalType.None);
    }
    setProceeding(false);
  };

  useEffect(() => {
    (async () => {
      const [
        countriesResult,
        usStatesResult,
        stockExchangesResult,
        verticalTypesResult,
        validationRulesResult,
      ] = await Promise.allSettled([
        getCountries(),
        getUsStates(),
        getStockExchanges(),
        getVerticalTypes(),
        getValidationRules(),
      ]);
      if (countriesResult.status === 'fulfilled') {
        setCountries(countriesResult.value);
      }
      if (usStatesResult.status === 'fulfilled') {
        setUsStates(usStatesResult.value);
      }
      if (stockExchangesResult.status === 'fulfilled') {
        setStockExchanges(stockExchangesResult.value);
      }
      if (verticalTypesResult.status === 'fulfilled') {
        setVerticalTypes(verticalTypesResult.value);
      }
      if (validationRulesResult.status === 'fulfilled') {
        setValidationRules(validationRulesResult.value);
      }
    })();
  }, []);

  useEffect(() => {
    if (brandDetailsFormValues.entityType === EntityTypes.SoleProprietor) {
      setBrandRelationshipFormValues({
        ...brandRelationshipFormValues,
        brandRelationshipScore: 1,
      });
      setContactDetailsFormValues({
        ...contactDetailsFormValues,
        businessContactEmail: '',
      });
    }
  }, [brandDetailsFormValues.entityType]);

  return (
    <section className="csp-verification-section brands">
      <Container maxWidth={false} style={{ padding: '0px' }}>
        <Stepper activeIndex={step} steps={Steps} />
        {step === 0 && (
          <BrandDetailsForm
            countryOptions={countries}
            usStateOptions={usStates}
            stockExchangeOptions={stockExchanges}
            verticalTypeOptions={verticalTypes}
            validationRules={validationRules}
            defaultValues={brandDetailsFormValues}
            onNext={(values) => {
              setBrandDetailsFormValues(values);
              setStep(step + 1);
            }}
          />
        )}
        {step === 1 && (
          <BrandRelationshipForm
            entityType={brandDetailsFormValues.entityType}
            defaultValues={brandRelationshipFormValues}
            onChange={(values) => setBrandRelationshipFormValues(values)}
            onPrevious={() => {
              setStep(step - 1);
            }}
            onNext={() => {
              setStep(step + 1);
            }}
          />
        )}
        {step === 2 && (
          <ContactDetailsForm
            entityType={brandDetailsFormValues.entityType}
            defaultValues={contactDetailsFormValues}
            onChange={(values) => setContactDetailsFormValues(values)}
            onPrevious={() => {
              setStep(step - 1);
            }}
            onNext={() => {
              setModalType(ModalType.Confirmation);
            }}
          />
        )}
      </Container>
      <HelpSection path="/brands/create" />
      <ConfirmationDialog
        open={modalType === ModalType.Confirmation}
        loader={proceeding}
        content={paymentInfoText}
        handleClick={(flag) => {
          if (flag) {
            proceed();
          } else {
            setModalType(ModalType.None);
          }
        }}
      />
      <SuccessDialogue
        open={modalType === ModalType.Success}
        icon={`${S3_ASSETS_PATH}/images/brand-success.svg`}
        title="Congratulations!"
        content={successInfoText}
        handleOk={() => history.push(`/brands/${registeredBrandUid}`)}
      />
    </section>
  );
};

export default BrandRegistration;
